import React, { useState } from "react";

import {
  ONBOARDING_INTERESTED_INDUSTRY_META,
  ONBOARDING_INTERESTED_INDUSTRY_VALUES,
  ONBOARDING_INVESTING_YEAR_META,
  ONBOARDING_INVESTING_YEAR_VALUES,
  type T_OnboardingInterestedIndustry,
  type T_OnboardingInvestingYears,
} from "@lattice/core-lib/types/onboarding/onboarding.types";

import { Button } from "@lattice/frontend/components/kit/Button";
import { Pill } from "@lattice/frontend/components/kit/Pill";
import { trpcApi } from "@lattice/frontend/lib/trpc/client";

import {
  SettingsPanelError,
  SettingsPanelLoading,
  SettingsPanelSuccess,
  SettingsSection,
} from "./SettingsPanelState";

export type T_InvestorProfilePanelViewProps = {
  error: string | null;
  industries: T_OnboardingInterestedIndustry[];
  investingYears: T_OnboardingInvestingYears;
  onSave: (profile: {
    industries: T_OnboardingInterestedIndustry[];
    investingYears: T_OnboardingInvestingYears;
  }) => void;
  saving: boolean;
  saved: boolean;
};

export function InvestorProfilePanelView({
  error,
  industries: initialIndustries,
  investingYears: initialInvestingYears,
  onSave,
  saving,
  saved,
}: T_InvestorProfilePanelViewProps) {
  const [investingYears, setInvestingYears] = useState(initialInvestingYears);
  const [industries, setIndustries] = useState(initialIndustries);

  const toggleIndustry = (industry: T_OnboardingInterestedIndustry) => {
    setIndustries((current) =>
      current.includes(industry)
        ? current.filter((candidate) => candidate !== industry)
        : [...current, industry],
    );
  };

  return (
    <div className="shell-settings-form">
      <p className="shell-settings__body">
        Prism uses this profile to tailor research context. You can update it at
        any time.
      </p>

      <SettingsSection label="Investing experience">
        <div className="shell-settings-pill-grid">
          {ONBOARDING_INVESTING_YEAR_VALUES.map((value) => (
            <Pill
              key={value}
              label={ONBOARDING_INVESTING_YEAR_META[value].text}
              onClick={() => setInvestingYears(value)}
              selected={investingYears === value}
            />
          ))}
        </div>
      </SettingsSection>

      <SettingsSection label="Industries and themes">
        <div className="shell-settings-pill-grid">
          {ONBOARDING_INTERESTED_INDUSTRY_VALUES.map((value) => (
            <Pill
              key={value}
              label={ONBOARDING_INTERESTED_INDUSTRY_META[value].text}
              onClick={() => toggleIndustry(value)}
              selected={industries.includes(value)}
            />
          ))}
        </div>
      </SettingsSection>

      {error ? <SettingsPanelError message={error} /> : null}
      {saved ? (
        <SettingsPanelSuccess message="Investor profile saved." />
      ) : null}
      <Button
        disabled={industries.length === 0}
        label="Save profile"
        loading={saving}
        onClick={() => onSave({ industries, investingYears })}
      />
    </div>
  );
}

export function InvestorProfilePanel() {
  const utils = trpcApi.useUtils();
  const profileQuery = trpcApi.users.get_investor_profile.useQuery(undefined, {
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
  });
  const updateProfile = trpcApi.users.update_investor_profile.useMutation({
    onSuccess: async () => {
      await utils.users.get_investor_profile.invalidate();
    },
  });

  if (profileQuery.isPending) {
    return <SettingsPanelLoading label="Loading investor profile" />;
  }
  if (profileQuery.isError || !profileQuery.data) {
    return (
      <SettingsPanelError message="Investor profile could not be loaded." />
    );
  }

  const profile = profileQuery.data.data;
  return (
    <InvestorProfilePanelView
      key={`${profile.investing_years}:${profile.interested_industries.join(",")}`}
      error={updateProfile.error?.message ?? null}
      industries={profile.interested_industries}
      investingYears={profile.investing_years}
      onSave={({ industries, investingYears }) =>
        updateProfile.mutate({
          interested_industries: industries,
          investing_years: investingYears,
        })
      }
      saved={updateProfile.isSuccess}
      saving={updateProfile.isPending}
    />
  );
}
